import { supabase } from './supabase';

export interface DownloadOptions {
  fileName?: string;
  mimeType?: string;
}

/**
 * Downloads a file from Supabase Storage
 * @param filePath - The path to the file in Supabase Storage
 * @param options - Optional download options
 */
export const downloadFileFromStorage = async (
  filePath: string, 
  options: DownloadOptions = {}
): Promise<void> => {
  try {
    // Extract bucket name and file path
    const pathParts = filePath.split('/');
    if (pathParts.length < 2) {
      throw new Error('Invalid file path format. Expected: bucket/path/to/file');
    }
    
    const bucketName = pathParts[0];
    const filePathInBucket = pathParts.slice(1).join('/');
    
    // Download the file
    const { data, error } = await supabase.storage
      .from(bucketName)
      .download(filePathInBucket);
    
    if (error) {
      console.error('Error downloading file:', error);
      throw new Error(`Error downloading file: ${error.message}`);
    }
    
    if (!data) {
      throw new Error('No file data received');
    }
    
    // Create download link
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = options.fileName || filePathInBucket.split('/').pop() || 'download';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    console.log('File downloaded successfully:', options.fileName || filePathInBucket);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

/**
 * Downloads a file from a URL (for external files)
 * @param url - The URL of the file to download
 * @param fileName - The name for the downloaded file
 */
export const downloadFileFromUrl = async (
  url: string, 
  fileName: string
): Promise<void> => {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(downloadUrl);
    
    console.log('File downloaded successfully from URL:', fileName);
  } catch (error) {
    console.error('Download from URL failed:', error);
    throw error;
  }
};

/**
 * Downloads a document from application metadata
 * @param doc - The document metadata
 */
export const downloadApplicationDocument = async (doc: any): Promise<void> => {
  try {
    console.log('Attempting to download document:', doc);
    
    // Use the detection function for better handling
    await detectAndDownloadDocument(doc);
    
  } catch (error) {
    console.error('Error downloading application document:', error);
    
    // Show user-friendly error message
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Error desconocido al descargar el archivo';
    
    alert(`Error al descargar el documento: ${errorMessage}`);
    throw error;
  }
};

/**
 * Detects the type of document and downloads accordingly
 * @param doc - The document metadata
 */
const detectAndDownloadDocument = async (doc: any): Promise<void> => {
  // Check if document has a file_path (stored in Supabase Storage)
  if (doc.file_path) {
    console.log('Downloading from Supabase Storage:', doc.file_path);
    await downloadFileFromStorage(doc.file_path, {
      fileName: doc.file_name || doc.title,
      mimeType: doc.mime_type
    });
    return;
  }
  
  // Check if document has a direct URL
  if (doc.file_url) {
    console.log('Downloading from URL:', doc.file_url);
    await downloadFileFromUrl(doc.file_url, doc.file_name || doc.title);
    return;
  }
  
  // Check if document has a file_name that might be a URL
  if (doc.file_name && (doc.file_name.startsWith('http://') || doc.file_name.startsWith('https://'))) {
    console.log('Downloading from file_name URL:', doc.file_name);
    await downloadFileFromUrl(doc.file_name, doc.title || 'documento');
    return;
  }
  
  // Check if document has a title that might be a URL
  if (doc.title && (doc.title.startsWith('http://') || doc.title.startsWith('https://'))) {
    console.log('Downloading from title URL:', doc.title);
    await downloadFileFromUrl(doc.title, doc.file_name || 'documento');
    return;
  }
  
  // For documents without file_path or file_url, create a placeholder download
  console.log('No file path or URL available, creating placeholder download');
  await createPlaceholderDownload(doc);
};

/**
 * Creates a placeholder download for documents without actual files
 * @param doc - The document metadata
 */
const createPlaceholderDownload = async (doc: any): Promise<void> => {
  try {
    // Create a simple text file with document information
    const content = `Documento: ${doc.title || doc.file_name || 'Documento'}
Tipo: ${doc.type || 'No especificado'}
Tamaño: ${doc.file_size ? `${doc.file_size} bytes` : 'No especificado'}
Subido: ${doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : 'No especificado'}
Estado: ${doc.signed ? 'Firmado' : 'Pendiente de firma'}

Este es un documento de referencia. El archivo original no está disponible para descarga.
Para obtener el archivo real, contacte al administrador del sistema.`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${doc.title || doc.file_name || 'documento'}.txt`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    
    console.log('Placeholder document downloaded successfully');
  } catch (error) {
    console.error('Error creating placeholder download:', error);
    throw new Error('No se pudo crear el documento de referencia');
  }
};

/**
 * Gets the public URL for a file in Supabase Storage
 * @param filePath - The path to the file in Supabase Storage
 * @param expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 */
export const getPublicFileUrl = async (
  filePath: string, 
  expiresIn: number = 3600
): Promise<string> => {
  try {
    const pathParts = filePath.split('/');
    if (pathParts.length < 2) {
      throw new Error('Invalid file path format. Expected: bucket/path/to/file');
    }
    
    const bucketName = pathParts[0];
    const filePathInBucket = pathParts.slice(1).join('/');
    
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePathInBucket, expiresIn);
    
    if (error) {
      throw new Error(`Error creating signed URL: ${error.message}`);
    }
    
    return data.signedUrl;
  } catch (error) {
    console.error('Error getting public file URL:', error);
    throw error;
  }
};
